import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Copy, MessageCircle, Smartphone, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NO_ONE = 'No one yet';

// "Sat 24 Oct 2026" in UK time
const ukLongDate = (value) => {
  if (!value) return '';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    timeZone: 'Europe/London',
  });
};

// "15 Oct 2026 14:32" in UK time
export const ukStamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Europe/London',
  }).replace(',', '');
};

// UK 07... becomes 447... for WhatsApp
const internationalNumber = (phone) => {
  const digits = String(phone || '').replace(/[^\d+]/g, '').replace(/^\+/, '');
  if (!digits) return '';
  if (digits.startsWith('44')) return digits;
  if (digits.startsWith('0')) return `44${digits.slice(1)}`;
  if (digits.startsWith('7') && digits.length === 10) return `44${digits}`;
  return digits;
};

const buildMessage = (job) => {
  const times = [job.time_start, job.time_end].filter(Boolean).join('-')
    || String(job.time || '').trim();
  const client = [job.client_name, job.client_phone].filter(Boolean).join(', ');
  return [
    'Butler Direct booking',
    job.job_type ? `Job: ${job.job_type}` : '',
    ukLongDate(job.date) ? `Date: ${ukLongDate(job.date)}` : '',
    times ? `Time: ${times}` : '',
    job.location ? `Address: ${job.location}` : '',
    job.guest_count ? `Guests: ${job.guest_count}` : '',
    client ? `Client: ${client}` : '',
    'Please reply to confirm. Full details are in the staff app: https://staff.butlerdirect.co.uk',
  ].filter(Boolean).join('\n');
};

export const BookingTextDialog = ({ job, token, open, onClose, onLogged }) => {
  const assigned = job.assigned_staff_details || [];
  const [allStaff, setAllStaff] = useState([]);
  const [chosen, setChosen] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);

  const options = useMemo(() => (
    assigned.length > 0
      ? assigned.map((staff) => ({ name: staff.name, phone: staff.phone }))
      : [{ name: NO_ONE, phone: '' },
         ...allStaff.map((staff) => ({ name: staff.name, phone: staff.phone }))]
  ), [assigned, allStaff]);

  useEffect(() => {
    if (!open) return;
    setMessage(buildMessage(job));
    setCopied(false);
    setChosen(assigned.length > 0 ? assigned[0].name : NO_ONE);
    if (assigned.length === 0) {
      axios.get(`${API}/admin/staff`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setAllStaff(res.data || []))
        .catch(() => setAllStaff([]));
    }
    /* eslint-disable-next-line */
  }, [open, job.id]);

  const selected = options.find((option) => option.name === chosen) || { name: chosen, phone: '' };
  const number = internationalNumber(selected.phone);

  const log = async (method) => {
    try {
      const res = await axios.post(`${API}/admin/jobs/${job.id}/booking-text`,
        { staff_name: selected.name || NO_ONE, method },
        { headers: { Authorization: `Bearer ${token}` } });
      onLogged && onLogged(res.data.booking_texts_sent);
    } catch (err) {
      toast.error('Sent, but we could not save it to the job');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
    } catch (err) {
      const box = document.createElement('textarea');
      box.value = message;
      document.body.appendChild(box);
      box.select();
      document.execCommand('copy');
      document.body.removeChild(box);
    }
    setCopied(true);
    toast.success('Copied');
    log('Copy');
  };

  const openWhatsApp = () => {
    const base = number ? `https://wa.me/${number}` : 'https://wa.me/';
    window.open(`${base}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    log('WhatsApp');
  };

  const openSms = () => {
    window.location.href = `sms:${number || ''}?&body=${encodeURIComponent(message)}`;
    log('Text');
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-lg" data-testid="booking-text-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Create booking text</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">
              {assigned.length > 0 ? 'Which staff member is this for?' : 'Send to'}
            </label>
            <select value={chosen} onChange={(e) => setChosen(e.target.value)}
                    className="w-full" data-testid="booking-text-staff">
              {options.map((option) => (
                <option key={option.name} value={option.name}>
                  {option.name}{option.phone ? ` · ${option.phone}` : ''}
                </option>
              ))}
            </select>
          </div>

          <Textarea
            rows={10}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="font-mono text-xs"
            data-testid="booking-text-message"
          />

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={copy} data-testid="booking-text-copy">
              {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}
              {copied ? 'Copied' : 'Copy text'}
            </Button>
            <Button size="sm" onClick={openWhatsApp} data-testid="booking-text-whatsapp">
              <MessageCircle className="mr-1 h-4 w-4" /> Send on WhatsApp
            </Button>
            <Button size="sm" variant="outline" onClick={openSms} data-testid="booking-text-sms">
              <Smartphone className="mr-1 h-4 w-4" /> Send as text
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingTextDialog;
